import React from "react";
import { TessaMessageRendererProps } from "@webV2/pages/Tessa/types";
import {
  AiChatToolGetUsersWorkingLocationsOutput,
  AiChatToolGetUsersWorkingLocationsPart,
  ISchedule,
  IUserFull
} from "@gettactic/api";
import { ChatBotToolCard } from "@webV2/pages/Tessa/ChatBotToolCard";
import { ScheduleInfoCard } from "@webV2/pages/settings/WorkplaceRequestsPage/components/RequestDetailsModal/ScheduleInfoCard";
import { cn, getObjectKeys } from "@gettactic/helpers";
import { MapWithMiniOfficePhoto } from "@webV2/components/dashboard/DashboardEventDetails/MapWithMiniOfficePhoto";
import { useAskTessa } from "@v2/components/ai/AskTessaProvider";
import { UserAvatar } from "@v2/components/display/UserAvatar/UserAvatar";

const ScheduleBoxById: React.FC<{
  schedule: ISchedule;
  user: IUserFull;
  setSelectedSchedule: (schedule: { userId: string; id: string }) => void;
  isSelected?: boolean;
}> = ({ schedule, user, setSelectedSchedule, isSelected }) => {
  if (!schedule) return null;
  return (
    <div
      className={"max-w-[300px]"}
      onClick={() => setSelectedSchedule({ userId: user.id, id: schedule.id })}
    >
      <div className={"absolute -mt-1 z-20"}>
        <UserAvatar
          size="xs"
          user={{
            name: user.name,
            profile_photo_url: user.profile_photo_url
          }}
        />
      </div>
      <ScheduleInfoCard
        key={schedule.id}
        schedule={schedule}
        className={cn("m-0", isSelected ? "bg-neutral-100" : undefined)}
      />
    </div>
  );
};

export const ChatBotToolShowUsersWorkingLocations: React.FC<
  TessaMessageRendererProps<AiChatToolGetUsersWorkingLocationsPart>
> = ({ part }) => {
  const output = JSON.parse(part?.output || "null") as
    | AiChatToolGetUsersWorkingLocationsOutput
    | undefined;

  let initialSchedule = { userId: "", id: "" };
  for (const userId in output?.users_locations || {}) {
    if (output?.users_locations?.[userId].schedule_ids?.length) {
      initialSchedule = {
        userId,
        id: output?.users_locations[userId].schedule_ids[0]
      };
      break;
    }
  }

  const [selectedSchedule, setSelectedSchedule] = React.useState<{
    userId: string;
    id: string;
  }>(initialSchedule);
  if (
    initialSchedule.id &&
    !selectedSchedule.id &&
    selectedSchedule.id !== initialSchedule.id
  ) {
    setSelectedSchedule(initialSchedule);
  }

  const askTessa = useAskTessa();
  const userIds = output ? getObjectKeys(output.users_locations || {}) : [];
  const scheduleId = output?.users_locations?.[
    selectedSchedule.userId
  ]?.schedule_ids?.find((id) => id === selectedSchedule.id);

  const schedule = output?._metadata?.schedules_by_id?.[scheduleId || ""];
  const isToolFullScreen = askTessa?.fullPageToolDisclosure.isOpen ?? false;
  if (!output || (userIds.length || 0) === 0) return null;

  return (
    <ChatBotToolCard toolId={part.toolCallId} className={"w-full max-w-full"}>
      <div className={"flex flex-wrap gap-4"}>
        {userIds.map((userId) => {
          const scheduleIds = output?.users_locations?.[userId]?.schedule_ids;
          const user = output?._metadata?.users_by_id?.[userId];
          if (!user || !scheduleIds || !scheduleIds.length) return null;
          return scheduleIds.map((scheduleId) => {
            const schedule = output?._metadata?.schedules_by_id?.[scheduleId];
            if (!schedule) {
              return null;
            }
            return (
              <ScheduleBoxById
                key={scheduleId}
                schedule={schedule}
                user={user}
                setSelectedSchedule={setSelectedSchedule}
                isSelected={
                  selectedSchedule.userId === userId &&
                  selectedSchedule.id === scheduleId
                }
              />
            );
          });
        })}
      </div>
      {schedule && (
        <div className={"mt-4"}>
          <MapWithMiniOfficePhoto
            resourceId={schedule.resource.id}
            officeId={schedule.resource.office_id}
            parentId={schedule.resource.parent_id ?? ""}
            startDate={schedule.start.split("T")[0]}
            resourceImage={undefined}
            showMiniMap={false}
            addMainClassName={!isToolFullScreen}
            className={
              isToolFullScreen ? "absolute inset-0 top-40 border-t" : undefined
            }
          />
        </div>
      )}
    </ChatBotToolCard>
  );
};
